import { SOPTemplate } from "@features/wx/dispatcher/types";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { CheckCircle, X } from "lucide-react";
import React from "react";

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSOP: SOPTemplate | null;
  selectedEntities: any[];
  priority: string;
  notes: string;
  onCreateAnother: () => void;
}

export const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  selectedSOP,
  selectedEntities,
  priority,
  notes,
  onCreateAnother,
}) => {
  if (!isOpen || !selectedSOP) return null;

  const generateServiceOrderId = () => {
    return `SO${Date.now().toString().slice(-6)}`;
  };

  const serviceOrderId = generateServiceOrderId();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-end mb-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Service Order Created Successfully!
          </h2>
          <p className="text-gray-600">
            Service Order ID:{" "}
            <span className="font-medium">{serviceOrderId}</span>
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Service Order Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">SOP Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">SOP Name:</span>
                  <span className="text-gray-900">{selectedSOP.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Association:</span>
                  <span className="text-gray-900">
                    {selectedSOP.association}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Estimated Time:</span>
                  <span className="text-gray-900">
                    {selectedSOP.estimatedTime}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">SLA:</span>
                  <span className="text-gray-900">{selectedSOP.sla}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">Order Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Priority:</span>
                  <span
                    className={`font-medium capitalize ${
                      priority === "critical"
                        ? "text-red-600"
                        : priority === "high"
                        ? "text-orange-600"
                        : priority === "medium"
                        ? "text-blue-600"
                        : "text-green-600"
                    }`}
                  >
                    {priority}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Entities Selected:</span>
                  <span className="text-gray-900">
                    {selectedEntities.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mode:</span>
                  <span className="text-gray-900">
                    {selectedEntities.length > 1 ? "Bulk" : "Single"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created On:</span>
                  <span className="text-gray-900">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Entities */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">
              Selected Entities
            </h3>
            <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {selectedEntities.map((entity, index) => (
                  <span
                    key={entity.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {selectedSOP.association === "Meter" && entity.meterNo}
                    {selectedSOP.association === "Consumer" && entity.name}
                    {selectedSOP.association === "Asset" && entity.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          {notes && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                <HoverRevealText text={notes} maxLength={70} />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center gap-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={onCreateAnother}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create Another Order
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
